import { useState, useEffect, useCallback } from 'react'
import { Search, ChevronLeft, ChevronRight, Loader2, Download, Leaf, Users, DollarSign, Cloud } from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchDonations, fetchDonationCampaignStats } from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'

interface Donation {
  id: string
  amount: number
  currency: string
  status: string
  method: string
  reference: string
  description?: string
  createdAt: string
  user: { id: string; name: string; email: string; phone: string | null } | null
}

interface CampaignStats {
  totalRaised: number
  treesPlanted: number
  treeGoal: number
  progressPercent: number
  totalDonations: number
  totalDonors: number
  co2OffsetKg: number
  costPerTree: number
}

const STATUS_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
]

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
}

const METHOD_LABELS: Record<string, string> = {
  MTN_MOMO: 'MTN MoMo',
  TELECEL_CASH: 'Telecel Cash',
  AIRTELTIGO_MONEY: 'AirtelTigo Money',
  CARD: 'Card',
  BANK_TRANSFER: 'Bank Transfer',
  CASH: 'Cash',
}

const PAGE_SIZE = 10

export default function Donations() {
  const [donations, setDonations] = useState<Donation[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [page, setPage] = useState(1)
  const [campaign, setCampaign] = useState<CampaignStats | null>(null)

  const loadDonations = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchDonations({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: searchQuery || undefined,
        page,
        limit: PAGE_SIZE,
      })
      setDonations(data?.donations ?? [])
      setTotal(data?.pagination?.total ?? 0)
    } catch {
      toast.error('Failed to load donations')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, searchQuery, page])

  const loadCampaign = useCallback(async () => {
    try {
      const data = await fetchDonationCampaignStats()
      setCampaign(data)
    } catch {
      // Campaign stats are non-critical
    }
  }, [])

  useEffect(() => { loadDonations() }, [loadDonations])
  useEffect(() => { loadCampaign() }, [loadCampaign])

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearchQuery(search)
      setPage(1)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const costPerTree = campaign?.costPerTree ?? 10

  return (
    <div className="space-y-5">
      {/* Campaign Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-xs text-[#6B7280] font-medium">Total Raised</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">GHS {(campaign?.totalRaised ?? 0).toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <Leaf className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-xs text-[#6B7280] font-medium">Trees Planted</p>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {(campaign?.treesPlanted ?? 0).toLocaleString()}
            <span className="text-sm font-semibold text-[#6B7280]"> / {(campaign?.treeGoal ?? 5000).toLocaleString()}</span>
          </p>
          <div className="mt-2 h-2 bg-green-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, campaign?.progressPercent ?? 0)}%` }}
            />
          </div>
          <p className="text-[10px] text-[#6B7280] mt-1">{campaign?.progressPercent ?? 0}% of goal reached</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-xs text-[#6B7280] font-medium">Total Donors</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{campaign?.totalDonors ?? 0}</p>
          <p className="text-[10px] text-[#6B7280] mt-1">{campaign?.totalDonations ?? 0} total donations</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <Cloud className="w-4 h-4 text-teal-600" />
            </div>
            <p className="text-xs text-[#6B7280] font-medium">CO2 Offset</p>
          </div>
          <p className="text-2xl font-bold text-teal-700">{(campaign?.co2OffsetKg ?? 0).toLocaleString()} kg</p>
          <p className="text-[10px] text-[#6B7280] mt-1">22 kg per tree annually</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-[#6B7280]" />
          <input
            type="text"
            placeholder="Search by donor name or email... (press Enter)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => { setStatusFilter(value); setPage(1) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                statusFilter === value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Donations Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} donation{total !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            onClick={() => {
              if (!donations.length) return
              exportToCsv(
                ['ID', 'Donor Name', 'Email', 'Phone', 'Amount (GHS)', 'Trees', 'Method', 'Status', 'Reference', 'Date'],
                donations.map((d) => [
                  d.id,
                  d.user?.name ?? '',
                  d.user?.email ?? '',
                  d.user?.phone ?? '',
                  String(d.amount),
                  String(Math.floor(d.amount / costPerTree)),
                  METHOD_LABELS[d.method] ?? d.method,
                  d.status,
                  d.reference ?? '',
                  new Date(d.createdAt).toLocaleDateString(),
                ]),
                'donations'
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Reference', 'Donor', 'Amount', 'Method', 'Date', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {donations.map((donation) => {
                  const st = STATUS_STYLES[donation.status] ?? STATUS_STYLES.PENDING
                  const trees = Math.floor(donation.amount / costPerTree)
                  return (
                    <tr key={donation.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono font-semibold text-primary-700">
                        {donation.reference?.slice(0, 12) ?? donation.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[#1A1A2E]">{donation.user?.name ?? '—'}</p>
                        <p className="text-[10px] text-[#6B7280]">{donation.user?.email ?? ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-[#1A1A2E]">
                          GHS {donation.amount?.toLocaleString() ?? 0}
                        </span>
                        <p className="text-[10px] text-emerald-600 font-medium">
                          = {trees} tree{trees !== 1 ? 's' : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {METHOD_LABELS[donation.method] ?? donation.method}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(donation.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${st.bg} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {donation.status}
                        </span>
                      </td>
                    </tr>
                  )
                })}
                {donations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No donations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    p === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
